import React, { useEffect, useMemo, useState } from 'react';
import { Card, Space, Tag, Spin, Empty } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment';
import { getHealthHabitIndicators, getHealthHabitIndex } from '@/services/healthHabit';
import styles from './index.less';

const palette = {
  sport: '#177ddc',
  diet: '#fa8c16',
  sleep: '#722ed1',
  mental: '#52c41a',
  under: '#ff4d4f',
  over: '#13c2c2',
  missing: 'rgba(0,0,0,0.25)',
};

function last21Range() {
  const end = moment().endOf('day');
  const start = moment().subtract(20, 'days').startOf('day');
  return {
    periodStart: start.format('YYYY-MM-DD'),
    periodEnd: end.format('YYYY-MM-DD'),
    days: Array.from({ length: 21 }).map((_, i) => start.clone().add(i, 'days')),
  };
}

function lastWeekRange() {
  const end = moment().subtract(1, 'weeks').endOf('week');
  const start = moment().subtract(1, 'weeks').startOf('week');
  return {
    periodStart: start.format('YYYY-MM-DD'),
    periodEnd: end.format('YYYY-MM-DD'),
  };
}

function normalize(val) {
  if (val === '') return null;
  if (Array.isArray(val)) return val.map((v) => normalize(v));
  if (val && typeof val === 'object') {
    const next = {};
    Object.keys(val).forEach((k) => {
      next[k] = normalize(val[k]);
    });
    return next;
  }
  return val;
}

function toSeries(series, color) {
  return (series || []).map((item) => {
    let label = item?.key || '-';
    const missing = item == null;
    return { date: label, value: missing ? '缺失' : item, color, missing };
  });
}

function buildTrendConfig(data, themeColor) {
  return {
    data,
    xField: 'date',
    yField: 'value',
    height: 160,
    autoFit: true,
    padding: [12, 12, 24, 12],
    appendPadding: [0, 8, 0, 8],
    color: themeColor,
    tooltip: {
      showMarkers: false,
      customContent: (title, items) => {
        const datum = items?.[0]?.data;
        const label = datum?.missing ? '缺失' : datum?.value;
        return `<div style="padding:8px 12px"><div style="font-weight:600">${title}：${label}</div></div>`;
      },
    },
    state: {
      active: { style: { opacity: 0.85 } },
    },
    interactions: [{ type: 'element-active' }],
    animation: {
      appear: { animation: 'scale-in-y', duration: 500 },
    },
    label: false,
    meta: { value: { nice: true } },
    xAxis: { label: null, tickLine: null },
    yAxis: { label: { autoHide: false, autoRotate: false } },
  };
}

function EBar({ config, seriesKey }) {
  const ref = React.useRef(null);
  const chart = React.useRef(null);
  useEffect(() => {
    const echarts = window?.echarts;
    if (!echarts || !ref.current) return;
    if (!chart.current) chart.current = echarts.init(ref.current);
    const labels = (config?.data || []).map((d) => d?.date ?? '');
    const values = (config?.data || []).map((d) => d?.value ?? '');
    chart.current.setOption({
      grid: { top: 16, right: 16, bottom: 16, left: 16, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          const name = p?.name ?? '';
          const val = p?.value ?? '-';
          return `${name}<br/>${String(val)}`;
        },
      },
      xAxis: { 
        type: 'category', 
        data: labels, 
        axisLabel: { show: false }, 
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { show: true },
        axisLine: { show: true },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      series: [
        {
          type: 'bar',
          data: values,
          itemStyle: { color: config?.color || palette.sport },
          barMaxWidth: 20,
        },
      ],
    });
    const onResize = () => chart.current && chart.current.resize();
    window.addEventListener('resize', onResize);
    
    // ResizeObserver logic
    const observer = new ResizeObserver(() => {
        onResize();
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      chart.current && chart.current.dispose();
      chart.current = null;
    };
  }, [config]);
  return <div style={{ height: config?.height || 160, width: '100%' }} ref={ref} />;
}

function ELine({ actual, baseline, labels, colorA, colorB }) {
  const ref = React.useRef(null);
  const chart = React.useRef(null);
  useEffect(() => {
    const echarts = window?.echarts;
    if (!echarts || !ref.current) return;
    if (!chart.current) chart.current = echarts.init(ref.current);
    chart.current.setOption({
      grid: { top: 24, right: 24, bottom: 16, left: 56, containLabel: true },
      tooltip: { 
        trigger: 'axis',
      },
      legend: { top: 0, data: ['实际', '目标'], icon: 'circle', textStyle: { color: '#64748b', fontSize: 12 } },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { show: true, color: '#94a3b8', fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { show: true, margin: 10, color: '#94a3b8', formatter: (v) => String(v) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: 'rgba(148,163,184,0.35)' } },
      },
      series: [
        {
          name: '实际',
          type: 'line',
          smooth: true,
          data: actual,
          lineStyle: { width: 3, color: colorA || '#06b6d4' },
          areaStyle: {
            color: new window.echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(6,182,212,0.35)' },
              { offset: 1, color: 'rgba(6,182,212,0.05)' },
            ]),
          },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: colorA || '#06b6d4', borderColor: '#ffffff', borderWidth: 2 },
        },
        {
          name: '目标',
          type: 'line',
          smooth: true,
          data: baseline,
          lineStyle: { width: 2, color: colorB || '#94a3b8', type: 'dashed' },
          symbol: 'circle',
          symbolSize: 4,
          itemStyle: { color: colorB || '#94a3b8' },
        },
      ],
    });
    const onResize = () => chart.current && chart.current.resize();
    window.addEventListener('resize', onResize);

    // ResizeObserver logic
    const observer = new ResizeObserver(() => {
        onResize();
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      chart.current && chart.current.dispose();
      chart.current = null;
    };
  }, [actual, baseline, labels, colorA, colorB]);
  return <div style={{ height: 220, width: '100%' }} ref={ref} />;
}

function EIndexDial({ value, levelDesc }) {
  const ref = React.useRef(null);
  const chart = React.useRef(null);
  useEffect(() => {
    const echarts = window?.echarts;
    if (!echarts || !ref.current) return;
    if (!chart.current) chart.current = echarts.init(ref.current);
    const MIN = 1.0;
    const MAX = 1.3;
    const raw = Number(value);
    const v = Number.isFinite(raw) ? Math.min(MAX, Math.max(MIN, raw)) : MIN;
    chart.current.setOption({
      tooltip: { show: false },
      series: [
        {
          type: 'gauge',
          min: MIN,
          max: MAX,
          startAngle: 210,
          endAngle: -30,
          radius: '90%',
          axisLine: {
            lineStyle: {
              width: 12,
              color: [[1, '#e6eef5']],
            },
          },
          pointer: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          progress: {
            show: true,
            roundCap: true,
            width: 12,
            itemStyle: { color: '#06b6d4' },
          },
          detail: {
            valueAnimation: true,
            formatter: () => String(levelDesc || '-'),
            color: '#0f172a',
            fontSize: 24,
            offsetCenter: [0, 0],
          },
          data: [{ value: v }],
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '60%',
          style: { text: `系数：${v.toFixed(1)}`, fill: '#64748b', fontSize: 12 },
        },
      ],
    });
    chart.current.resize();
    const onResize = () => chart.current && chart.current.resize();
    window.addEventListener('resize', onResize);

    // ResizeObserver logic
    const observer = new ResizeObserver(() => {
        onResize();
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      chart.current && chart.current.dispose();
      chart.current = null;
    };
  }, [value, levelDesc]);
  return <div style={{ height: 180, width: '100%' }} ref={ref} />;
}

function EChronoPie({ preferences }) {
  const ref = React.useRef(null);
  const chart = React.useRef(null);
  const keys = ['morning', 'afternoon', 'evening', 'night'];
  const labels = { morning: '上午', afternoon: '下午', evening: '晚上', night: '夜间' };
  const colors = ['#2fc3ff', '#13c2c2', '#3a5ad9', '#8a5cd9'];
  const data = React.useMemo(() => {
    return keys.map((k, i) => {
      const val = Number(preferences.find((p) => p.key === k)?.value || 0);
      return { value: val, name: labels[k], itemStyle: { color: colors[i] } };
    });
  }, [preferences]);

  const total = data.reduce((a, b) => a + b.value, 0);

  useEffect(() => {
    const echarts = window?.echarts;
    if (!echarts || !ref.current) return;
    if (!chart.current) chart.current = echarts.init(ref.current);
    
    chart.current.setOption({
      tooltip: { 
        trigger: 'item',
        formatter: '{b}: {d}%' 
      },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: (params) => {
              return `${params.name}\n${params.percent}%`;
            },
            color: '#64748b',
            fontSize: 11
          },
          labelLine: {
            show: true,
            length: 10,
            length2: 10 
          },
          data: data
        }
      ],
      graphic: []
    });
    const onResize = () => chart.current && chart.current.resize();
    window.addEventListener('resize', onResize);

    // ResizeObserver logic
    const observer = new ResizeObserver(() => {
        onResize();
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      chart.current && chart.current.dispose();
      chart.current = null;
    };
  }, [data, total]);

  return <div style={{ height: 220, width: '100%' }} ref={ref} />;
}

function metricCard(title, config, seriesKey) {
  return (
    <Card className={styles['hh-card']} hoverable bodyStyle={{ padding: 12 }} size="small" title={title}>
      {config?.data?.length ? (window?.echarts ? <EBar config={config} seriesKey={seriesKey} /> : <Empty description="ECharts未加载" />) : <Empty description="暂无数据" />}
    </Card>
  );
}

export default function HealthHabitView({ userId }) {
  const [loading, setLoading] = useState(true);
  const [idxLoading, setIdxLoading] = useState(true);
  const [indicators, setIndicators] = useState(null);
  const [idx, setIdx] = useState(null);
  const [renderCount, setRenderCount] = useState(14);

  const { periodStart, periodEnd, days } = useMemo(() => last21Range(), []);
  const lw = useMemo(() => lastWeekRange(), []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getHealthHabitIndicators({
      requestData: { userId, periodStart, periodEnd },
    })
      .then((res) => {
        if (!mounted) return;
        setIndicators(normalize(res?.data));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    setIdxLoading(true);
    getHealthHabitIndex({
      requestData: { userId, periodStart: lw.periodStart, periodEnd: lw.periodEnd, dimension: 'SPORT' },
    })
      .then((res) => {
        if (!mounted) return;
        setIdx(normalize(res?.data));
      })
      .finally(() => {
        if (mounted) setIdxLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [userId, periodStart, periodEnd, lw.periodStart, lw.periodEnd]);

  const metricItems = useMemo(() => {
    const items = [];
    const sport = indicators?.sport || {};
    const diet = indicators?.diet || {};
    const sleep = indicators?.sleep || {};
    const mental = indicators?.mental || {};

    const pushMetricByKey = (dim, groupObj, key, title, color) => {
      const series = Array.isArray(groupObj?.[key]) ? groupObj[key] : [];
      const data = toSeries(series, color);
      items.push({ dim, key, title, config: buildTrendConfig(data, color) });
    };

    pushMetricByKey('sport', sport, 'activeTimes', '运动-次数', palette.sport);
    pushMetricByKey('sport', sport, 'activeDuration', '运动-时长(分钟)', palette.sport);
    pushMetricByKey('sport', sport, 'steps', '运动-步数', palette.sport);
    pushMetricByKey('sport', sport, 'activeKilocalories', '运动-热量消耗(千卡)', palette.sport);

    pushMetricByKey('sleep', sleep, 'sleepHours', '睡眠-时长(小时)', palette.sleep);
    pushMetricByKey('sleep', sleep, 'rhr', '睡眠-静息心率', palette.sleep);
    pushMetricByKey('sleep', sleep, 'hrv', '睡眠-HRV', palette.sleep);

    pushMetricByKey('diet', diet, 'dietRecordTimes', '饮食-打卡次数', palette.diet);
    pushMetricByKey('diet', diet, 'dietTakenKilocalories', '饮食-热量摄入(千卡)', palette.diet);
    pushMetricByKey('diet', diet, 'dietTakenProtein', '饮食-蛋白质摄入(克)', palette.diet);
    pushMetricByKey('diet', diet, 'dietTakenFat', '饮食-脂肪摄入(克)', palette.diet);
    pushMetricByKey('diet', diet, 'dietTakenCho', '饮食-碳水化合物摄入(克)', palette.diet);

    pushMetricByKey('mental', mental, 'mentalRecordTimes', '心理-活动次数', palette.mental);
    pushMetricByKey('mental', mental, 'mentalRecordDuration', '心理-活动时长(分钟)', palette.mental);

    return items;
  }, [indicators, days]);

  const visibleItems = metricItems.slice(0, renderCount);
  const sport = indicators?.sport || {};
  const prefer = Array.isArray(sport?.activePreferences) ? sport.activePreferences : [];
  const normalizeVal = (v) => {
    if (v === '' || v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const targetItems = Array.isArray(idx?.targets) ? idx.targets : [];
  const scoreItems = Array.isArray(idx?.scores) ? idx.scores : [];
  const labelKeys = targetItems.map((d) => d?.key ?? d?.name ?? d?.metric ?? String(d?.id ?? ''));
  const scoreMap = new Map(
    scoreItems.map((d) => [d?.key ?? d?.name ?? d?.metric ?? String(d?.id ?? ''), normalizeVal(d?.value)])
  );
  const idxTargetsVals = labelKeys.map((k) => normalizeVal(targetItems.find((d) => (d?.key ?? d?.name ?? d?.metric ?? String(d?.id ?? '')) === k)?.value));
  const idxScoresVals = labelKeys.map((k) => scoreMap.get(k) ?? 0);
  const idxLabels = labelKeys;

  return (
    <div className={styles['health-habit']}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div className={styles['hh-section']}>
          <div className={styles['hh-header']}>
            <div className={styles['hh-cell-gauge']}>
              <div className={styles['hh-index-title']}>运动习惯指数</div>
              {window?.echarts ? (
                <EIndexDial value={idx?.habitCoefficient ?? 0} levelDesc={idx?.habitLevelDesc || ''} />
              ) : (
                <div className={styles['hh-index-value']}>{idx?.habitCoefficient ?? '-'}</div>
              )}
            </div>
            <div className={styles['hh-cell-chrono']}>
              <div className={styles['hh-index-title']}>过去21天运动时段偏好</div>
              {window?.echarts ? <EChronoPie preferences={prefer} /> : <Empty description="ECharts未加载" />}
            </div>
            <div className={styles['hh-cell-trend']}>
              <div className={styles['hh-index-title']}>过去6周运动达标趋势</div>
              {window?.echarts ? (
                <ELine actual={idxScoresVals} baseline={idxTargetsVals} labels={idxLabels} colorA="#13c2c2" colorB="#94a3b8" />
              ) : (
                <Empty description="ECharts未加载" />
              )}
            </div>
          </div>
        </div>
      
        <InfiniteScroll
          dataLength={visibleItems.length}
          next={() => setRenderCount((n) => Math.min(metricItems.length, n + 6))}
          hasMore={visibleItems.length < metricItems.length}
          loader={<div style={{ textAlign: 'center', padding: 12 }}><Spin /></div>}
          scrollThreshold={0.9}
        >
          {loading && !indicators ? (
            <Card><Spin /></Card>
          ) : (
            <div className={styles['hh-grid']}>
              {visibleItems.map((it, i) => (
                <div key={`${it.dim}-${it.key}-${i}`}>
                  {metricCard(it.title, it.config, it.key)}
                </div>
              ))}
            </div>
          )}
        </InfiniteScroll>
      </Space>
    </div>
  );
}
